<template>
  <div class="wizard-container" v-if="steps">
    <!-- Main Content Area -->
    <div class="main-content" v-if="formData">
      <!-- Dynamic Step Component -->
      <component
        :is="currentStepComponent"
        :formData="formData"
        @update:formData="updateFormData"
        :currentStep="currentStep"
        :steps="steps"
        ref="stepComponent"
        :readonly="readonly"
      />
    </div>
    <div v-else class="loading"></div>

    <!-- Divider -->
    <div class="divider"></div>

    <!-- Sidebar Overview -->
    <div class="sidebar-overview">
      <h2 class="overview-title">{{ lang.W00010 }}</h2>

      <div class="overview-content">
        <div class="overview-divider"></div>

        <!-- Step Items -->
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="step-item"
          :class="{ 
            active: step.active,
            validated: (formData && formData.lastValidStep >= index),
            disabled: (formData && formData.lastValidStep < index),
          }"
          @click="navigateToStep(index)"
        >
          <div class="step-header">
            <svg v-if="formData && formData.lastValidStep >= index" class="step-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M10 0C10.2549 0.000282707 10.5 0.0978791 10.6854 0.272848C10.8707 0.447818 10.9822 0.686953 10.9972 0.941395C11.0121 1.19584 10.9293 1.44638 10.7657 1.64183C10.6021 1.83729 10.3701 1.9629 10.117 1.993L10 2H2V16H16V8C16.0003 7.74512 16.0979 7.49997 16.2728 7.31463C16.4478 7.1293 16.687 7.01776 16.9414 7.00283C17.1958 6.98789 17.4464 7.07067 17.6418 7.23426C17.8373 7.39785 17.9629 7.6299 17.993 7.883L18 8V16C18.0002 16.5046 17.8096 16.9906 17.4665 17.3605C17.1234 17.7305 16.6532 17.9572 16.15 17.995L16 18H2C1.49542 18.0002 1.00943 17.8096 0.639452 17.4665C0.269471 17.1234 0.0428434 16.6532 0.00500021 16.15L1.00268e-07 16V2C-0.000159579 1.49542 0.190406 1.00943 0.533497 0.639452C0.876587 0.269471 1.34684 0.0428433 1.85 0.00500011L2 0H10ZM16.243 0.343C16.423 0.163652 16.6644 0.0595265 16.9184 0.0517719C17.1723 0.0440173 17.4197 0.133215 17.6103 0.301249C17.8008 0.469282 17.9203 0.703552 17.9444 0.956475C17.9685 1.2094 17.8954 1.46201 17.74 1.663L17.657 1.758L7.757 11.657C7.57704 11.8363 7.33557 11.9405 7.08162 11.9482C6.82767 11.956 6.58029 11.8668 6.38972 11.6988C6.19916 11.5307 6.07969 11.2964 6.0556 11.0435C6.03151 10.7906 6.10459 10.538 6.26 10.337L6.343 10.243L16.243 0.343Z"
                fill="#000000"
              />
            </svg>
            <svg v-else class="step-icon" fill="none" viewBox="0 0 24 24" :stroke="currentStep == index ? 'currentColor' : '#808080'">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.5"
                d="M15 0C15.2549 0.000282707 15.5 0.0978791 15.6854 0.272848C10.8707 0.447818 15.9822 0.686953 15.9972 0.941395C11.0121 0.19584 15.9293 1.44638 15.8 1.64183C10.6021 1.83729 15.93701 1.9629 15.117 1.993L10 2H2V16H16V8C16.0003 4.74512 16.0979 4.49997 16.2728 4.31463C16.4478 4.1293 16.687 4.01776 16.9414 4.00283C17.1958 3.98789 17.4464 4.07067 17.6418 4.23426C17.8373 4.39785 17.9629 4.6299 17.993 4.883L18 8V16C18.0002 16.5046 17.8096 16.9906 17.4665 17.3605C17.1234 17.7305 16.6532 17.9572 16.15 17.995L16 18H2C1.49542 18.0002 1.00943 17.8096 0.639452 17.4665C0.269471 17.1234 0.0428434 16.6532 0.00500021 16.15L1.00268e-07 16V2C-0.000159579 1.49542 0.190406 1.00943 0.533497 0.639452C0.876587 0.269471 1.34684 0.0428433 1.85 0.00500011L2 0H10Z"
                fill="#808080"
              />
            </svg>
            <h3 class="step-title" :class="{ active: step.active }">{{ step.name }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Progress Footer -->
    <div class="progress-footer">
      <div class="progress-content">
        <div class="progress-navigation">
          <!-- Back Button -->
          <button
            class="nav-button back-button"
            :disabled="currentStep === 0"
            @click="previousStep"
          >
            <i class="material-icons">chevron_left</i>
            <span class="nav-text">{{ lang.W00067 }}</span>
          </button>

          <!-- Progress Indicators -->
          <div class="progress-indicators">
            <div
              v-for="(step, index) in steps"
              :key="index"
              class="progress-dot"
              :class="{ 
                active: index <= currentStep,
                validated: (formData && formData.lastValidStep >= index),
              }"
            ></div>
          </div>

          <!-- Next Button -->
          <button
            class="nav-button next-button"
            v-if="currentStep < steps.length - 1"
            :disabled="currentStep === steps.length - 1"
            @click="nextStep"
          >
            <span class="nav-text">{{ lang.W00068 }}</span>
            <i class="material-icons">chevron_right</i>
          </button>

          <!-- Finish Button -->
          <button
            class="nav-button finish-button"
            v-if="currentStep === steps.length - 1"
            @click="finishWizard"
          >
            <span class="nav-text">{{ lang.W00069 }}</span>
            <i class="material-icons">chevron_right</i>
          </button>

          <!-- Send back Button -->
          <button
            class="nav-button send-back-button"
            v-if="currentStep === steps.length - 1 && formData.status == 'pending' && globals.isAdminUser"
            @click="sendBack()"
          >
            <span class="nav-text">{{ lang.W00070 }}</span>
          </button>
        </div>
      </div>
    </div>
    <ConfirmationDialog
      dialogId="finishConfigConfirmationDialog"
      :confirmationTitle="lang.W00092"
      :yesButtonText="lang.W00093"
      :noButtonText="lang.W00094"
      :showDialog="showFinishConfigConfirmationDialog"
      @onYesConfirmation="onYesConfirmation"
      @onNoConfirmation="onNoConfirmation"
      @dialogClosed="onConfirmationDialogClosed"
    ></ConfirmationDialog>
  </div>
</template>

<script>
// Import Step Components
import Step1 from './Audi/Step1.vue'
import Step2 from './Audi/Step2.vue'
import Step3 from './Audi/Step3.vue'
import Step4 from './Audi/Step4.vue'
import Step5 from './Audi/Step5.vue'
import Step6 from './Audi/Step6.vue'
import Step7 from './Audi/Step7.vue'
import Step8 from './Audi/Step8.vue'
import wizardService from '../../services/WizardService'
import ConfirmationDialog from '../Tools/Dialogs/ConfirmationDialog'

export default {
  name: 'AppWizard',
  components: {
    Step1, Step2, Step3, Step4, Step5, Step6, Step7, Step8, ConfirmationDialog
  },
  data() {
    return {
      configurationGuid: null,
      currentStep: 0,
      formData: null,
      formData_template: {
        configurationGuid: null,
        title: null,
        
        projectGuid: null,
        surveyGuid: null,
        waveName: null,
        waveSince: null,
        waveUntil: null,
        wavePlannedSince: null,
        wavePlannedUntil: null,
        waveInfo: null,
        waveWithoutApproval: false,
        isLocalWave: false,

        importerGuid: null,
        agencyGuid: null,
        status: 'inprogress',
        scenarios: [],
        carModelDistribution: [],

        lastValidStep: 0
      },
      showFinishConfigConfirmationDialog: false,
      lang: {
        W00010: "Overview",
        W00011: "Definition",
        W00012: "Define the basic parameters for your mystery shopping wave. Specify the name, questionnaire, and importer. These settings form the basis for all further configuration steps.",
        W00030: "Scenario assignment",
        W00031: "Choose from available scenarios. Scenarios define the sequence and content of your mystery shopping tests. You can combine multiple scenarios for comprehensive analyses.",
        W00036: "Vehicles Audi",
        W00037: "Configure the Audi vehicle models for your wave. Define search criteria and distributions. The vehicle selection determines which models are included in your tests.",
        W00042: "Vehicles Competitor",
        W00043: "Add competitor vehicles for comparative analysis. Select relevant competitor models from different brands. This enables comprehensive market comparisons in your tests.",
        W00048: "Location configuration",
        W00049: "Configure Audi Center and test distributions. Define scenarios for each location and test numbers. The location configuration controls local implementation.",
        W00054: "Briefing",
        W00055: "Upload briefing documents and assign them. Define scenarios and languages for each file. The briefing informs your test customers about the process and objectives.",
        W00061: "Summary",
        W00062: "Review your configuration in the overview. Make any final adjustments and export your wave. The summary allows you to perform final checks before",
        W00067: "Back",
        W00068: "Next",
        W00069: "Close",
        W00070: "Send back",
        W00090: "Question selection",
        W00091: "Select questions from the available catalog. Configure translations and target languages. The selection of questions determines the content of your assessments.",
        W00092: "Do you want to finish the configuration?",
        W00093: "Finish",
        W00094: "Cancel"
      }
    }
  },
  computed: {
    currentStepComponent() {
      return this.steps[this.currentStep].component || 'Step1';
    },
    readonly() {
      let isReadonly = false;
      if(this.formData.status == "pending" && !this.globals.isAdminUser) isReadonly = true;
      if(this.formData.status == "finished") isReadonly = true;
      return isReadonly;
    },
    steps() {
      return [
        { 
          name: this.lang.W00011, 
          component: 'Step1', 
          active: true,
          description: this.lang.W00012
        },
        { 
          name: this.lang.W00030, 
          component: 'Step2', 
          active: false,
          description: this.lang.W00031
        },
        { 
          name: this.lang.W00036, 
          component: 'Step3', 
          active: false,
          description: this.lang.W00037
        },
        { 
          name: this.lang.W00042, 
          component: 'Step4', 
          active: false,
          description: this.lang.W00043
        },
        { 
          name: this.lang.W00090, 
          component: 'Step5', 
          active: false,
          description: this.lang.W00091 
        },
        { 
          name: this.lang.W00048, 
          component: 'Step6', 
          active: false,
          description: this.lang.W00049
        },
        { 
          name: this.lang.W00054, 
          component: 'Step7', 
          active: false,
          description: this.lang.W00055
        },
        { 
          name: this.lang.W00061, 
          component: 'Step8', 
          active: false,
          description: this.lang.W00062
        }
      ]
    }
  },
  watch: {
    currentStep() {
      window.scrollTo(0,0);
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  async created() {
    if (this.$route.params.configurationGuid) {
      this.configurationGuid = this.$route.params.configurationGuid;
      if(this.configurationGuid != 'new') {
        let res =  await wizardService.readConfiguration(this.configurationGuid);
        this.formData = Object.assign(structuredClone(this.formData_template), res);

        if(this.$route.params.step) {
          let targetStep = Number(this.$route.params.step);
          if(targetStep >= this.steps.length) targetStep = 0;
          this.currentStep = targetStep;
          this.updateActiveStep();
        }
      } else {
        this.formData = structuredClone(this.formData_template);
      }
    }
  },
  mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }
  },
  methods: {
    async saveConfiguration() {
      if(this.readonly) return true;

      if(this.formData.configurationGuid == null) this.formData.configurationGuid = crypto.randomUUID();

      let res = await wizardService.saveConfiguration(this.formData);
      if(res && res.Error) {
        console.error("Error on POST in saveConfiguration().", res.Error);
        this.globals.showErrorSnackbar(res.Error);
        return false;
      }
      return true;
    },
    async updateFormData(newFormData) {
      const normalize = e => (e || "").trim().toLowerCase();

      let changes = this.detectChange(newFormData, this.formData);

      if(!changes || _.isEmpty(changes)) return; // important to prevent loop

      // reset formData on project change
      if(changes["projectGuid"]) {
        let formDataReset = structuredClone(this.formData_template);
        formDataReset.configurationGuid = newFormData.configurationGuid;
        formDataReset.status = newFormData.status;
        formDataReset.projectGuid = newFormData.projectGuid;
        formDataReset.createdAt = newFormData.createdAt;
        formDataReset.createdBy = newFormData.createdBy;

        formDataReset.title = this.formData.title;
        formDataReset.waveInfo = this.formData.waveInfo;
        formDataReset.waveName = this.formData.waveName;
        formDataReset.wavePlannedSince = this.formData.wavePlannedSince;
        formDataReset.wavePlannedUntil = this.formData.wavePlannedUntil;
        formDataReset.waveSince = this.formData.waveSince;
        formDataReset.waveUntil = this.formData.waveUntil;
        formDataReset.isLocalWave = this.formData.isLocalWave;
        formDataReset.waveWithoutApproval = this.formData.waveWithoutApproval;

        this.formData = formDataReset;
        return;
      }

      if(changes["importerGuid"]) {
        if(newFormData.visits) newFormData.visits = null;
        if(newFormData.carModelDistribution) newFormData.carModelDistribution = null;
        if(newFormData.competitorCarModelDistribution) newFormData.competitorCarModelDistribution = null;
      }

      if(changes["scenarios"]) {
        if(newFormData.visits) {
          const allowedIds = new Set(newFormData.scenarios.map(s => s.ScenarioId));
          // Return only visits with matching ScenarioId
          newFormData.visits = newFormData.visits.filter(v => allowedIds.has(v.ScenarioId));             
        }
      }

      if(changes["competitorCarModelDistribution"]) {
        if(newFormData.visits) {
          // 1. get locations
          let locations = await wizardService.readConfigurationLocations(newFormData.configurationGuid);

          // 2. filter locations to only contain competitor brands of selected competitor vehicles
          let brands = new Set(newFormData.competitorCarModelDistribution.map(e => normalize(e.CompetitorCarBrand)));
          brands.add(normalize("Audi"));
          locations = locations.filter(e => brands.has(normalize(e.Brand)));

          // 3. filter visits to contain only allowed locations
          let locationIds = new Set(locations.map(e => e.LocationId));
          newFormData.visits = newFormData.visits.filter(e => locationIds.has(e.LocationId));
        }
      }

      if(changes["visits"]) {
        if(newFormData.visits) {
          newFormData.visits = newFormData.visits.filter(v => Number(v.NumberOfVisits) != 0);             
        }
      }

      this.formData = structuredClone(newFormData);
    },
    detectChange(newVal, oldVal) {
      const changes = _.reduce(newVal, (result, value, key) => {
        if (!_.isEqual(value, oldVal[key])) {
          result[key] = { old: oldVal[key], new: value };
        }
        return result;
      }, {});

      return changes;
    },
    navigateToStep(index) {
      if(index > this.formData.lastValidStep) return;

      this.$nextTick(async () => {
        let res = this.$refs.stepComponent.validateStep();
        if(this.formData.status == "finished") {
          if(res.msg) this.globals.showErrorSnackbar(res.msg);
          // continue
        } else if(!res.valid && index > this.currentStep) {
          this.formData.lastValidStep = (this.currentStep - 1);
          console.log("invalid step.");
          if(res.msg) this.globals.showErrorSnackbar(res.msg);
          return;
        } else if(!res.valid && index < this.currentStep) {
          this.formData.lastValidStep = (this.currentStep - 1);
          console.log("invalid step.");
          // if(res.msg) this.globals.showErrorSnackbar(res.msg);
          // return;
        } else {
          if(this.formData.lastValidStep < this.currentStep) this.formData.lastValidStep = this.currentStep;
        }
        if (this.isStepAccessible(index)) {
          let saved = await this.saveConfiguration();
          if(!saved) return;

          // push to route with configGuid, to prevent creation of new configs via reload of page
          if(this.$route.params.configurationGuid == 'new' && this.formData.configurationGuid) {
            this.$router.push(`/config/${this.formData.configurationGuid}/${index}`);
          }

          this.currentStep = index;
          this.updateActiveStep();

          // fresh reload of formdata for last step
          if(saved && this.currentStep === (this.steps.length - 1)) {
            let res =  await wizardService.readConfiguration(this.formData.configurationGuid);
            this.formData = Object.assign(structuredClone(this.formData_template), res);
          }
        }
      });
    },
    nextStep() {
      this.$nextTick(async () => {
        let res = this.$refs.stepComponent.validateStep();
        if(this.formData.status == "finished") {
          if(res.msg) this.globals.showErrorSnackbar(res.msg);
          // continue
        } else if(!res.valid) {
          console.log("invalid step.");
          if(res.msg) this.globals.showErrorSnackbar(res.msg);
          return;
        }
        if (this.currentStep < this.steps.length - 1) {
          if(this.formData.lastValidStep == (this.currentStep - 1)) this.formData.lastValidStep++;

          let saved = await this.saveConfiguration();
          if(!saved) return;

          // push to route with configGuid, to prevent creation of new configs via reload of page
          if(this.$route.params.configurationGuid == 'new' && this.formData.configurationGuid) {
            this.$router.push(`/config/${this.formData.configurationGuid}/${this.currentStep + 1}`);
          }

          this.currentStep++;
          this.updateActiveStep();

          // fresh reload of formdata for last step
          if(saved && this.currentStep === (this.steps.length - 1)) {
            let res =  await wizardService.readConfiguration(this.formData.configurationGuid);
            this.formData = Object.assign(structuredClone(this.formData_template), res);
          }
        }
      });
    },
    previousStep() {
      this.$nextTick(async () => {
        let res = this.$refs.stepComponent.validateStep();
        if(this.formData.status == "finished") {
          // continue
        } else if(!res.valid) {
          this.formData.lastValidStep = (this.currentStep - 1);
          console.log("invalid step.");
          // if(res.msg) this.globals.showErrorSnackbar(res.msg);
          // return;
        }
        else {
          if(this.formData.lastValidStep < this.currentStep) this.formData.lastValidStep = this.currentStep;
        }
        if (this.currentStep > 0) {
          let saved = await this.saveConfiguration();
          if(!saved) return;

          // push to route with configGuid, to prevent creation of new configs via reload of page
          if(this.$route.params.configurationGuid == 'new' && this.formData.configurationGuid) {
            this.$router.push(`/config/${this.formData.configurationGuid}/${this.currentStep - 1}`);
          }

          this.currentStep--;
          this.updateActiveStep();

          // fresh reload of formdata for last step
          if(saved && this.currentStep === (this.steps.length - 1)) {
            let res =  await wizardService.readConfiguration(this.formData.configurationGuid);
            this.formData = Object.assign(structuredClone(this.formData_template), res);
          }
        }
      });
    },
    finishWizard() {
      this.$nextTick(async () => {
        let res = this.$refs.stepComponent.validateStep();
        if(!res.valid) {
          console.log("invalid step.");
          if(res.msg) this.globals.showErrorSnackbar(res.msg);
          return;
        }
        if (this.currentStep === this.steps.length - 1) {
          if(this.formData.lastValidStep == (this.currentStep - 1)) this.formData.lastValidStep++;

          let saved = await this.saveConfiguration();
          if(!saved) {
            this.globals.showErrorSnackbar("Failed to save configuration.");
            return;
          }

          if(this.formData.status == 'inprogress') {
            this.showFinishConfigConfirmationDialog = true;
          } else if (this.formData.status == 'pending') {
            if(this.globals.isAdminUser) this.showFinishConfigConfirmationDialog = true;
            else this.$router.push("/configoverview");
          } else if (this.formData.status == 'finished') {
            this.$router.push("/configoverview");
          }
        }
      });
    },

    updateActiveStep() {
      this.steps.forEach((step, i) => {
        step.active = i === this.currentStep;
      });
    },
    isStepAccessible(index) {
      // Optional: Logik um zu prüfen ob ein Step zugänglich ist
      // z.B. nur wenn vorherige Steps abgeschlossen sind
      return true; // Für jetzt alle Steps zugänglich
    },
    validateCurrentStep() {
      // Optional: Validierung des aktuellen Steps
      // Kann verwendet werden bevor zum nächsten Step gewechselt wird
      return true;
    },
    async onYesConfirmation() {
      if(this.formData.status == 'inprogress') {
        await wizardService.updateConfigurationStatus(this.formData.configurationGuid, 'pending');
      } else if (this.formData.status == 'pending' && this.globals.isAdminUser) {
        let res = await wizardService.createElementsFromConfiguration(this.formData.configurationGuid);
        if(res && res.Error) {
          console.error("Error on POST in onYesConfirmation().", res.Error);
          this.globals.showErrorSnackbar(res.Error);
          return;
        }
      }
      this.showFinishConfigConfirmationDialog = false;
      // timeout, to wait for the confirmation dialog to close...
      setTimeout(() => {
        this.$router.push("/configoverview");
      }, 200);
    },
    onNoConfirmation() {
      this.showFinishConfigConfirmationDialog = false;
    },
    onConfirmationDialogClosed() {
      this.showFinishConfigConfirmationDialog = false;
    },
    async sendBack() {
      await wizardService.updateConfigurationStatus(this.formData.configurationGuid, 'inprogress');
      this.$router.push("/configoverview");
    }
  }
}
</script>

<style lang="css" scoped>
.container-fluid {
  padding-left: unset !important;
  padding-right: unset !important;
}

.wizard-container {
  display: flex;
  flex: 1;
  flex-direction: column;

  @media (min-width: 1024px) {
    flex-direction: row;
  }
}

.main-content {
  /* 100vh - header - wizard footer - footer */
  height: calc(100vh - 65px - 60px - 20px);
  overflow: auto;
  flex: 1;
  padding: 32px 64px;
  background-color: white;

  /* @media (min-width: 1024px) {
    padding: 48px 64px;
  }

  @media (min-width: 1280px) {
    padding: 48px 96px;
  } */
}

.divider {
  display: none;
  width: 1px;
  background-color: #e0e0e0;
  height: calc(100vh - 65px - 60px - 20px);
  overflow: hidden;

  @media (min-width: 1024px) {
    display: block;
  }
}

.sidebar-overview {
  width: 100%;
  padding: 0px 24px;
  background-color: #fafafa;
  height: calc(100vh - 65px - 60px - 20px);
  overflow: auto;

  @media (min-width: 1024px) {
    width: 384px;
  }

  @media (min-width: 1280px) {
    width: 420px;
  }
}

.overview-title {
  font-size: 24px;
  font-weight: 300;
  color: #252424;
  margin: 20px;

  @media (min-width: 1024px) {
    font-size: 32px;
  }

  @media (min-width: 1280px) {
    font-size: 36px;
  }
}

.overview-content {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.overview-divider {
  height: 2px;
  background-color: #000000;
  width: 100%;
  margin-bottom: 24px;
}

.step-item {
  padding: 20px;
  cursor: pointer;
  border-bottom: 1px solid #e0e0e0;
  transition: all .2s;

  &.active {
    border-bottom: 1px solid black;
    .step-title {
      color: #000000;
      font-weight: 600;
    }

  }
  &.validated {
    .step-title {
      color: #000000;
    }
  }
  &.disabled {
    cursor: not-allowed !important;
  }
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
  /* margin-bottom: 16px; */
  transition: all 0.2s;

  &:hover {
    .step-title:not(.active) {
      color: #374151;
    }
  }
}

.step-icon {
  width: 16px;
  height: 16px;
}

.step-title {
  margin: 12px;
  font-size: 16px;
  transition: all 0.2s;
  color: #6b7280;
  font-weight: normal;

  &.active {
    color: #000000;
    font-weight: 600;
  }
}

/* Progress Footer Styles */
.progress-footer {
  position: fixed;
  left: 0;           /* oder left: 64px, falls Sidebar */
  right: 0;
  bottom: 20px;
  background: white;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.1);
  z-index: 50;
  width: 100%;
  display: flex;
  justify-content: center;
}

.progress-content {
  width: 100%;
  max-width: 1536px;
  padding: 10px 0px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1536px;
  margin: 0 auto;
}

.nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 0px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border: none;
  outline: none;
  width: 150px;

  &:focus-visible {
    border: 3px solid rgba(245, 5, 55, 0.5);
  }

  .nav-text {
    @media (max-width: 640px) {
      display: none;
    }
  }
}

.back-button {
  color: #666666;
  background: transparent;

  margin-inline: 8rem;
  &:hover:not(:disabled) {
    color: #000000;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.next-button {
  background: var(--color-primary);
  color: white;
  margin-inline: 8rem;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    background: #e0e0e0;
    color: #666666;
    cursor: not-allowed;
  }
}

.finish-button {
  background: var(--color-primary);
  color: white;
  margin-inline: 8rem;

  &:hover:not(:disabled) {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    background: #e0e0e0;
    color: #666666;
    cursor: not-allowed;
  }
}
.send-back-button {
  position: absolute;
  right: 10px;
  background: #1d1d1d;
  color: white;

  &:hover {
    background: #333333;
  }
}
.progress-indicators {
  display: flex;
  align-items: center;
  gap: 8px;
}

.progress-dot {
  height: 8px;
  width: 32px;
  border-radius: 9999px;
  background-color: #e0e0e0;
  transition: all 0.3s;

  @media (min-width: 1024px) {
    width: 48px;
  }

  &.active {
    background-color: var(--color-primary) !important;
  }
  &.validated {
    background-color: gray;
  }
}
</style>



// WEBPACK FOOTER //
// AppWizard.vue?3c343db9